import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSessionToken } from './useSessionToken';
import { isRevokedError } from './useRevokedKickOut';
import {
  buildStopAnswerLength,
  ConversationRole,
  FRAMEWORKS,
  MAX_VOICE_CLIENT_EVENT_MESSAGE_LENGTH,
  MAX_VOICE_SESSION_DURATION_MS,
  VOICE_TURN_SEPARATOR,
  VoiceClientEvent,
  VoiceSessionPhase,
  type ConversationTurn,
  type QuestionType,
  type RealtimeControlFrame,
  type VoiceSessionClientEvent,
  type VoiceTurnTiming,
} from '@evaltick/shared';
import { VoiceService, ConversationsService, BaseAPIClient } from '@/services/api';
import { ConversationEntryKind, type ConversationEntry } from '@/store/interviewStore';

/**
 * Where a live voice session is, across its phases (see the voice
 * architecture plan). Asking/Answering/Finalizing/Feedback only ever occur
 * before an answer has been captured; AnswerCaptured/Conversing/Paused only
 * occur after one has — including immediately on a fresh connection, when the
 * server reports `initialPhase: "discussing"` because this question was already
 * answered (by voice or text) before this connection even started.
 */
export const VoicePhase = {
  Idle: 'idle',
  Connecting: 'connecting',
  /**
   * The coach is reading the question aloud — the opening turn on a fresh
   * attempt, when the server reports `initialPhase: "asking"`. Mic muted
   * throughout, so the coach can't hear itself and the candidate can't start
   * answering over it; `output_audio_buffer.stopped` is the one way out.
   */
  Asking: 'asking',
  /** Recording the initial answer — the model is configured to never reply until Stop. */
  Answering: 'answering',
  /** Stop was just clicked — mic muted, waiting on the last transcript(s) and the feedback nudge round trip. */
  Finalizing: 'finalizing',
  /** The coach is speaking its spoken feedback. */
  Feedback: 'feedback',
  /** Feedback finished; mic still muted until Record is clicked again. */
  AnswerCaptured: 'answer-captured',
  /** Hands-free follow-up conversation — mic live, model replies freely. */
  Conversing: 'conversing',
  /** Muted mid-follow-up (Stop clicked during Conversing) — same connection, no teardown. */
  Paused: 'paused',
  /**
   * Connection torn down, transcript preserved by the caller. Four ways in:
   * the panel unmounted (a mode switch away from voice), or — reached without
   * unmounting — the length cap ended a live call, either because the coach
   * finished inside the feedback reserve or because the server's own hangup
   * dropped the connection there (see VOICE_FEEDBACK_RESERVE_MS), or the call
   * dropped while idle in AnswerCaptured/Paused, where nothing was lost.
   * toggle() reconnects from here the same way in every case.
   */
  Suspended: 'suspended',
  /**
   * The one silent retry after a dropped call: the old connection is
   * torn down, the transcript kept, and a fresh one is negotiating. If it fails
   * the hook lands in Error; if it succeeds, in whichever phase the server says
   * the question is in.
   */
  Reconnecting: 'reconnecting',
  Error: 'error',
} as const;
export type VoicePhase = (typeof VoicePhase)[keyof typeof VoicePhase];

/**
 * Why the browser says the mic isn't delivering audio. Muted clears itself once
 * the mic is unmuted, so it only needs telling; Disconnected (device unplugged)
 * never recovers on its own and needs a fresh mic.
 */
export const MicIssue = {
  Muted: 'muted',
  Disconnected: 'disconnected',
} as const;
export type MicIssue = (typeof MicIssue)[keyof typeof MicIssue];

export const VoiceActivity = {
  Idle: 'idle',
  Listening: 'listening',
  Thinking: 'thinking',
  Speaking: 'speaking',
} as const;
export type VoiceActivity = (typeof VoiceActivity)[keyof typeof VoiceActivity];

/**
 * Every phase from the moment Stop is clicked through to being fully answered
 * — including Suspended reached from one of these via the length cap (an
 * answer already given stays given; the cap ending the call early doesn't
 * reopen it). With coaching disabled nothing in any of them is ever
 * actionable again for this question — no spoken feedback, no resuming to
 * converse — which is what both consumers key off: VoiceAnswerPanel hides the
 * mic/label, and SessionInterview locks the mode switch and shows the scoring
 * pill. Shared so the two can never disagree about which phases those are.
 */
export const POST_ANSWERING_PHASES: ReadonlySet<VoicePhase> = new Set([
  VoicePhase.Finalizing,
  VoicePhase.Feedback,
  VoicePhase.AnswerCaptured,
  VoicePhase.Conversing,
  VoicePhase.Paused,
  VoicePhase.Suspended,
]);

export const CONNECTED_IDLE_PHASES: ReadonlySet<VoicePhase> = new Set([
  VoicePhase.AnswerCaptured,
  VoicePhase.Paused,
]);

/**
 * Phases where the mic is actually live and capturing speech — the initial
 * answer, and the hands-free follow-up conversation once it resumes talking.
 * Shared so VoiceAnswerPanel's own recording UI and SessionInterview's mode-
 * switch lock can't drift apart on what counts as "still talking": Conversing
 * being left out of the latter once let a candidate switch to text mid-
 * utterance and silently tear down the live connection.
 */
export const RECORDING_PHASES: ReadonlySet<VoicePhase> = new Set([
  VoicePhase.Answering,
  VoicePhase.Conversing,
]);

export interface UseRealtimeVoiceParams {
  assessmentId: string;
  /**
   * Names the question being practised. The backend builds the coach's
   * instructions from this item, so no prompt text travels up from the browser.
   */
  interviewTemplateItemId: string;
  /**
   * From the package's `coachingEnabled` setting. When false, Stop never
   * triggers spoken feedback and the candidate can never re-enter a live
   * follow-up conversation — this hook lands straight in `AnswerCaptured`
   * every time, so "Next Question" is the only path forward.
   */
  coachingEnabled: boolean;
  /** Picks the answer framework whose target length the Stop message states. */
  questionType: QuestionType;
  /** Seeds this session's transcript from the store — what a mode switch back into voice needs to resume rendering where it left off. */
  initialEntries: ConversationEntry[];
  /** Fired on every entries change, so the caller can mirror it into the store (the source of truth once this hook unmounts on a mode switch). */
  onEntriesChange: (entries: ConversationEntry[]) => void;
  /**
   * Fired the moment Stop finalizes the initial answer (Answering →
   * Finalizing) — the assembled transcript plus each turn's real timing, for
   * the caller to submit via the existing /agents/evaluate path. Fires
   * immediately, independent of how long the spoken feedback itself takes.
   */
  onAnswerCaptured: (transcript: string, turnTimings: VoiceTurnTiming[]) => void;
  /** Called when a turn flush or session start gets a 410 (package revoked); the caller kicks the candidate out. */
  onRevoked: (err: unknown) => void;
}

export interface UseRealtimeVoiceResult {
  phase: VoicePhase;
  activity: VoiceActivity;
  errorMessage: string | null;
  entries: ConversationEntry[];
  getRemoteAudioLevel: () => number;
  /**
   * The single Record/Stop action — behavior depends on the current phase:
   * Idle/Suspended/Error → connect; Answering → stop & capture; AnswerCaptured/Paused → resume conversing; Conversing → pause.
   * The caller is responsible for the MIN_CHARS gate on calling this during
   * Answering (same bar AnswerInput's Submit uses) — this hook only executes
   * the transition, it doesn't judge whether the answer is long enough yet.
   */
  toggle: () => void;
  /**
   * Set while the browser reports the mic muted or unplugged during a recording
   * phase (see MicIssue). Read from the track's mute/ended state, not volume —
   * a candidate thinking is silent too.
   */
  micIssue: MicIssue | null;
  /** An inline message for the candidate that isn't a phase of its own (a reconnect landed mid-answer, transcription keeps failing). */
  notice: string | null;
  /** True while recording and transcription keeps failing — the notice offers typing instead, so the caller has to allow it. */
  transcriptionFailing: boolean;
  /** True when Error was reached by losing a call, rather than by the mic or a failed first connect. */
  connectionLost: boolean;
  /**
   * Tears the call down and negotiates a fresh one, keeping the transcript.
   * Unlike toggle() (Stop, in Answering), it works in every phase — e.g. to get
   * a fresh mic after one was unplugged.
   */
  reconnect: () => void;
  /**
   * Adds server-built context (per-component timing, once scoring lands) as a
   * system message, without asking for a reply. Dropped if no call is open; a
   * reconnect gets the same facts in its instructions.
   */
  addSessionContext: (text: string) => void;
}

const DATA_CHANNEL_LABEL = 'realtime-channel';
// Grace window after muting for Stop, so a trailing transcription event (the
// candidate's last few words) still lands before the answer is assembled —
// transcription runs on its own pipeline and can finish slightly after the
// mic itself goes quiet.
const FINALIZE_GRACE_MS = 900;
// After the grace window, if any candidate answer turn is still transcribing
// (no CandidateTranscriptDone yet — more likely the longer the utterance),
// poll at this interval rather than assembling early and silently dropping
// that turn from the transcript handed to Evaluate.
const FINALIZE_POLL_INTERVAL_MS = 200;
// Hard cap on how long Stop will wait on a slow/stuck transcription before
// assembling anyway — bounds the wait so a dropped transcription event can't
// hang the candidate's Stop click indefinitely. Generous on purpose: this is
// a whole spoken answer, not one short utterance, so the last turn before
// Stop can itself run tens of seconds if the candidate didn't pause partway
// through it, and transcribing that much can genuinely take longer than a
// couple of seconds. Losing the tail of an answer that can never be
// resubmitted is a far worse outcome than a longer "Reviewing answer…" wait.
const MAX_FINALIZE_WAIT_MS = 20_000;
// Debounced so a burst of turns (a fast back-and-forth) doesn't fire one
// request per turn; forced immediately before any teardown so nothing is lost.
const TURN_FLUSH_DEBOUNCE_MS = 1500;
// Tail end of the server's length cap (maxSessionDurationMs, from the
// negotiate response) held back so a candidate still answering when the cap
// closes in gets their answer submitted and hears the coach's feedback before
// the session ends. Needs to cover the finalize wait (FINALIZE_GRACE_MS plus
// at most MAX_FINALIZE_WAIT_MS of transcription polling) and one spoken
// feedback turn. Deliberately a fixed duration rather than a fraction of the
// cap: feedback takes the same time whatever the cap is set to, so a fraction
// would over-reserve on a long cap and under-reserve on a short one.
const VOICE_FEEDBACK_RESERVE_MS = 90_000;
// Smallest share of the cap left usable for answering, for a cap short enough
// that the reserve above would otherwise consume most of it (a local test
// value). Stops the mic-stop timer from firing on connect.
const MIN_ANSWERING_SHARE_OF_CAP = 0.5;
// How early a dropped connection still reads as the server's planned hangup
// rather than a network failure. Deliberately small: the client starts its
// clock at the top of connect() while the server only arms its cap timer once
// negotiation returns, so at a real hangup the client has always measured more
// elapsed time than the cap, never less. This absorbs jitter and nothing more
// — widening it starts swallowing genuine failures near the end of a session
// and reporting them to the candidate as a clean finish.
const LENGTH_CAP_DROP_TOLERANCE_MS = 5_000;
const REMOTE_AUDIO_LEVEL_GAIN = 4.5;
const REMOTE_AUDIO_FFT_SIZE = 512;
// How long ICE/the peer connection may sit in 'disconnected' before it counts
// as a drop. Chrome can stay there ~30s or more before reaching 'failed'; most
// brief blips recover well inside this window.
const DISCONNECT_GRACE_MS = 5_000;
// How long the data channel may take to open, once the SDP answer is applied,
// before the call counts as dropped.
const DATA_CHANNEL_OPEN_TIMEOUT_MS = 10_000;
// A reconnect waits this long, at most, for the previous call's last turns to
// reach the server, so a hung request can't keep the retry from ever starting.
const RECONNECT_FLUSH_WAIT_MS = 5_000;
// Bounds the negotiate request: Connecting/Reconnecting lock every control, so
// a hung one would leave the candidate with nothing to click but Exit.
const NEGOTIATE_TIMEOUT_MS = 20_000;
// A silent retry's call has to stay up this long before a later drop earns
// another one; dying sooner counts as the retry failing.
const RECONNECT_STABLE_MS = 30_000;
// Bounds each transcript write and health report. Both are chained (a
// reconnect waits on the writes, the end beacon on the reports), so one
// request that never settles would otherwise hold up every one after it.
const BACKGROUND_REQUEST_TIMEOUT_MS = 10_000;
// How long the mic track must stay muted/ended before it counts as off — a
// fresh track reports muted until its first audio arrives, and a device can blip.
const MIC_OFF_GRACE_MS = 3_000;
// Transcription failures within one Answering window before the candidate is told.
const TRANSCRIPTION_FAILURE_NOTICE_THRESHOLD = 2;

const CONNECTION_LOST_MESSAGE = 'Voice connection lost — you can try again or type your answer.';
// The same loss once Stop has already submitted the answer (coaching on, mid
// Finalizing/Feedback), where "type your answer" would invite a second one.
const CONNECTION_LOST_SUBMITTED_MESSAGE = 'Voice connection lost — your answer was submitted.';
const CONNECT_TIMEOUT_MESSAGE = 'Could not connect voice practice — try again, or type your answer.';
// Voice mode never shows the finished transcript, so the candidate has no way
// to see where their answer got to. The notice quotes the end of what was saved.
const DROPPED_NOTICE_TAIL_WORDS = 6;
const droppedNotice = (savedAnswer: string): string => {
  const words = savedAnswer.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 'Reconnected. Keep going.';
  const tail = words.slice(-DROPPED_NOTICE_TAIL_WORDS).join(' ');
  const ellipsis = words.length > DROPPED_NOTICE_TAIL_WORDS ? '…' : '';
  return `Reconnected. Saved up to "${ellipsis}${tail}" — keep going.`;
};
const TRANSCRIPTION_FAILED_NOTICE = "We couldn't hear that — try again or type your answer.";
const MIC_DENIED_MESSAGE =
  'Microphone access was denied. Grant microphone permission to use voice practice.';
const MIC_NOT_FOUND_MESSAGE =
  'No microphone was found. Connect one and try again, or type your answer.';
const MIC_IN_USE_MESSAGE =
  'Your microphone is being used by another app. Close it and try again, or type your answer.';
const MIC_UNAVAILABLE_MESSAGE =
  'Could not start the microphone. Try again, or type your answer.';

const MicErrorName = {
  PermissionDenied: 'NotAllowedError',
  InsecureContext: 'SecurityError',
  NoDevice: 'NotFoundError',
  DeviceBusy: 'NotReadableError',
} as const;

const micErrorMessage = (error: unknown): string => {
  switch (error instanceof DOMException ? error.name : '') {
    case MicErrorName.PermissionDenied:
    case MicErrorName.InsecureContext:
      return MIC_DENIED_MESSAGE;
    case MicErrorName.NoDevice:
      return MIC_NOT_FOUND_MESSAGE;
    case MicErrorName.DeviceBusy:
      return MIC_IN_USE_MESSAGE;
    default:
      return MIC_UNAVAILABLE_MESSAGE;
  }
};

// The data-channel events this hook acts on. Azure sends many more (session
// lifecycle, turn detection, response bookkeeping); everything else is ignored.
const RealtimeEventType = {
  CandidateTranscriptDelta: 'conversation.item.input_audio_transcription.delta',
  CandidateTranscriptDone: 'conversation.item.input_audio_transcription.completed',
  CandidateTranscriptFailed: 'conversation.item.input_audio_transcription.failed',
  CoachTranscriptDelta: 'response.output_audio_transcript.delta',
  CoachTranscriptDone: 'response.output_audio_transcript.done',
  // Audio finishing playback — fires meaningfully after CoachTranscriptDone,
  // since the transcript text streams ahead of the audio that speaks it.
  OutputAudioStopped: 'output_audio_buffer.stopped',
  // Fires for every conversation item, messages included, in true creation
  // order — used purely to record that order (see ensureOrdered/itemOrderRef).
  ItemAdded: 'conversation.item.added',
  // Acks a session.update actually taking effect — see armMicEnableOnAck for
  // why the mic waits on this before going live.
  SessionUpdated: 'session.updated',
  // Surfaces a rejected response.create (e.g. "conversation already has an
  // active response") so the feedback nudge can be retried instead of
  // silently vanishing — this transport has no response.done to wait on.
  Error: 'error',
} as const;

// Azure's own role vocabulary on a conversation item (conversation.item.added's
// item.role) — distinct from this app's ConversationRole, since it's read off
// the wire before this hook has decided anything about the item.
const RealtimeItemRole = {
  User: 'user',
} as const;

// Outbound events this hook sends that aren't server-authored
// RealtimeControlFrames — see commitInputAudioBuffer, pauseConversing and
// sendSystemItem.
const RealtimeOutboundEventType = {
  InputAudioBufferCommit: 'input_audio_buffer.commit',
  ResponseCancel: 'response.cancel',
  ConversationItemCreate: 'conversation.item.create',
} as const;

type RealtimeEvent =
  | { type: typeof RealtimeEventType.CandidateTranscriptDelta; delta: string; itemId: string }
  | { type: typeof RealtimeEventType.CandidateTranscriptDone; transcript: string; itemId: string }
  | {
      type: typeof RealtimeEventType.CandidateTranscriptFailed;
      itemId: string;
      code: string | null;
      message: string | null;
    }
  | { type: typeof RealtimeEventType.CoachTranscriptDelta; delta: string; itemId: string }
  | { type: typeof RealtimeEventType.CoachTranscriptDone; transcript: string; itemId: string }
  | { type: typeof RealtimeEventType.OutputAudioStopped }
  | { type: typeof RealtimeEventType.ItemAdded; itemId: string; isCandidateItem: boolean }
  | { type: typeof RealtimeEventType.SessionUpdated }
  | { type: typeof RealtimeEventType.Error; message: string | null; code: string | null };

const isKeyedObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const parseRealtimeEvent = (raw: string): RealtimeEvent | null => {
  let payload: unknown;
  try {
    payload = JSON.parse(raw);
  } catch (err) {
    // Not logging `raw` — a frame body can carry the candidate's speech.
    console.warn('Ignoring unparseable realtime frame', err);
    return null;
  }
  if (!isKeyedObject(payload)) return null;

  const { type, transcript, delta, item_id: itemId } = payload;
  switch (type) {
    case RealtimeEventType.CandidateTranscriptDelta:
      return typeof delta === 'string' && typeof itemId === 'string'
        ? { type: RealtimeEventType.CandidateTranscriptDelta, delta, itemId }
        : null;
    case RealtimeEventType.CandidateTranscriptDone:
      return typeof transcript === 'string' && typeof itemId === 'string'
        ? { type: RealtimeEventType.CandidateTranscriptDone, transcript, itemId }
        : null;
    case RealtimeEventType.CandidateTranscriptFailed: {
      const error = payload.error;
      return typeof itemId === 'string'
        ? {
            type: RealtimeEventType.CandidateTranscriptFailed,
            itemId,
            code: isKeyedObject(error) && typeof error.code === 'string' ? error.code : null,
            message: isKeyedObject(error) && typeof error.message === 'string' ? error.message : null,
          }
        : null;
    }
    case RealtimeEventType.CoachTranscriptDelta:
      return typeof delta === 'string' && typeof itemId === 'string'
        ? { type: RealtimeEventType.CoachTranscriptDelta, delta, itemId }
        : null;
    case RealtimeEventType.CoachTranscriptDone:
      return typeof transcript === 'string' && typeof itemId === 'string'
        ? { type: RealtimeEventType.CoachTranscriptDone, transcript, itemId }
        : null;
    case RealtimeEventType.OutputAudioStopped:
      return { type: RealtimeEventType.OutputAudioStopped };
    case RealtimeEventType.ItemAdded: {
      const item = payload.item;
      return isKeyedObject(item) && typeof item.id === 'string'
        ? {
            type: RealtimeEventType.ItemAdded,
            itemId: item.id,
            isCandidateItem: item.role === RealtimeItemRole.User,
          }
        : null;
    }
    case RealtimeEventType.SessionUpdated:
      return { type: RealtimeEventType.SessionUpdated };
    case RealtimeEventType.Error: {
      const error = payload.error;
      return {
        type: RealtimeEventType.Error,
        message: isKeyedObject(error) && typeof error.message === 'string' ? error.message : null,
        code: isKeyedObject(error) && typeof error.code === 'string' ? error.code : null,
      };
    }
    default:
      return null;
  }
};

const TERMINAL_CONNECTION_STATES: readonly RTCPeerConnectionState[] = ['failed', 'closed'];
const TERMINAL_ICE_STATES: readonly RTCIceConnectionState[] = ['failed', 'closed'];

// Phases a drop is silently retried from, since the candidate is mid-activity.
// Elsewhere a retry could do something unasked (from Paused/AnswerCaptured it
// would land in Conversing with the mic hot), so those drops go to Suspended
// or Error and the candidate reconnects when ready.
const AUTO_RECONNECT_PHASES: ReadonlySet<VoicePhase> = new Set([
  VoicePhase.Asking,
  VoicePhase.Answering,
  VoicePhase.Conversing,
]);

// Trims a report field to the server's length cap.
const clip = (value: string | null | undefined): string | undefined =>
  value ? value.slice(0, MAX_VOICE_CLIENT_EVENT_MESSAGE_LENGTH) : undefined;

const waitAtMost = async (promise: Promise<unknown>, ms: number): Promise<void> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([
    promise,
    new Promise<void>((resolve) => {
      timer = setTimeout(resolve, ms);
    }),
  ]);
  clearTimeout(timer);
};

/**
 * The two moments this hook asks the model for a response. Both send the same
 * server-authored bare `response.create` frame; what differs is when, and what
 * the session instructions say that turn is (see realtimeVoiceInstructions.ts).
 * Tracked as one pending request rather than two, so the retry-on-error path
 * below covers both and can't drift.
 */
const RealtimeResponseRequest = {
  /** Opening turn — the coach reads the question aloud. */
  AskQuestion: 'ask-question',
  /** Post-Stop turn — the coach gives its spoken feedback. */
  Feedback: 'feedback',
} as const;
type RealtimeResponseRequest =
  (typeof RealtimeResponseRequest)[keyof typeof RealtimeResponseRequest];

// The error Azure returns when input_audio_buffer.commit finds nothing left to commit.
const REALTIME_EMPTY_COMMIT_CODE = 'input_audio_buffer_commit_empty';

const RESPONSE_REQUEST_RETRY_DELAY_MS = 400;
const MAX_RESPONSE_REQUEST_RETRIES = 2;

// Backstops on the opening question: without one, a response that never
// speaks leaves the mic muted and the button inert with nothing left to move
// them. Neither bounds playback itself — a question can be as long as an admin
// cares to write, and cutting one off mid-sentence to open the mic is worse
// than the failure being guarded against. So the window is armed against a
// *lack* of progress and disarmed the moment the coach is audibly speaking:
// from there `output_audio_buffer.stopped` ends the phase, however long the
// question runs.
/** Nothing came back at all — no transcript, no audio. */
const ASK_QUESTION_START_TIMEOUT_MS = 15_000;
/**
 * The transcript completed but `output_audio_buffer.stopped` never landed.
 * Re-armed at that point rather than running from connect, so it bounds only
 * the audio still queued behind a finished transcript.
 */
const ASK_QUESTION_TAIL_TIMEOUT_MS = 20_000;

// A session.update switching turn_detection profiles isn't instant to apply
// server-side — if the mic goes live before the server acks it, speech right
// after the switch gets evaluated under the stale profile (create_response
// still false) and never gets an automatic reply. Falls back to enabling
// anyway if the ack never arrives, so a dropped session.updated can't leave
// the mic stuck muted.
const SESSION_UPDATE_ACK_FALLBACK_MS = 500;

/** Which kind a newly-created entry gets, stamped once at creation and never revisited — immune to transcription lag and to a mode-switch teardown/reconnect. */
const kindForPhase = (phase: VoicePhase, role: ConversationRole): ConversationEntryKind => {
  // Asking counts as Answer territory even though the mic is muted through it:
  // the mic goes live on the same event that ends the phase, so a candidate
  // who starts talking on the tail of the question can have their first item
  // created a beat before the phase flips. Tagged FollowUp, those words would
  // be silently dropped from the transcript assembleAnswer submits.
  if (
    phase === VoicePhase.Asking ||
    phase === VoicePhase.Answering ||
    phase === VoicePhase.Finalizing
  ) {
    return ConversationEntryKind.Answer;
  }
  return role === ConversationRole.Candidate
    ? ConversationEntryKind.FollowUp
    : ConversationEntryKind.Feedback;
};

type ClientEventDetail = NonNullable<VoiceSessionClientEvent['detail']>;

export function useRealtimeVoice(params: UseRealtimeVoiceParams): UseRealtimeVoiceResult {
  const {
    assessmentId,
    interviewTemplateItemId,
    coachingEnabled,
    questionType,
    initialEntries,
    onEntriesChange,
    onAnswerCaptured,
    onRevoked,
  } = params;
  const { getToken } = useSessionToken();

  const [phase, setPhase] = useState<VoicePhase>(VoicePhase.Idle);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [entries, setEntries] = useState<ConversationEntry[]>(initialEntries);

  const [isCoachSpeaking, setIsCoachSpeaking] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [micLive, setMicLive] = useState(false);
  const [micTrackIssue, setMicTrackIssue] = useState<MicIssue | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const phaseRef = useRef<VoicePhase>(phase);
  phaseRef.current = phase;
  // setPhase that also updates phaseRef synchronously, for transitions a drop
  // handler may read before React re-renders (see sendChannelPayload, capture).
  const enterPhase = useCallback((next: VoicePhase) => {
    phaseRef.current = next;
    setPhase(next);
  }, []);

  // The synchronous source of truth for the transcript, not just a mirror of
  // `entries`. Turns arrive from the data channel, where several can finalize
  // within one React batch — and a batched setState updater doesn't run until
  // the following render, so nothing that has to act on the *resulting* array
  // in the same tick (finalizeEntry's flush, assembleAnswer) can read it out
  // of a state updater. Every mutation therefore computes the next array from
  // this ref and commits both at once, via commitEntries.
  const entriesRef = useRef<ConversationEntry[]>(entries);

  const commitEntries = useCallback((next: ConversationEntry[]) => {
    entriesRef.current = next;
    setEntries(next);
  }, []);

  useEffect(() => {
    onEntriesChange(entries);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries]);

  // Whatever this question's transcript already held before this connection
  // opened (earlier text turns, an earlier voice connection). Fixed history —
  // this connection only ever reads it as the prefix `entries` gets rebuilt
  // onto, never rewrites or reorders it.
  const historyEntriesRef = useRef<ConversationEntry[]>(initialEntries);
  // The realtime session's own item ids, in the true creation order
  // `conversation.item.added` reports them in — the one thing the server
  // itself is authoritative about, independent of which item's transcription
  // or generation happens to *finish* first. Append-only: an id is pushed the
  // first time it's seen (normally via ItemAdded; sometimes a delta/done
  // arrives first, the rare case ensureOrdered's comment covers) and never
  // moved afterward.
  const itemOrderRef = useRef<string[]>([]);
  // Each item's current entry, live or finalized, keyed by itemId — the only
  // way a turn is looked up or written. `entries` is rebuilt on every change
  // as historyEntriesRef's fixed prefix plus itemOrderRef's ids mapped through
  // this map, so a turn's rendered position is exactly when its id first
  // appeared in itemOrderRef, and finalizing it only ever replaces its map
  // value in place — never its position.
  const itemContentRef = useRef<Map<string, ConversationEntry>>(new Map());
  // Zero point every fresh connection is timestamped relative to, so answer
  // timings survive being recomputed later without depending on wall clock.
  const sessionStartAtMsRef = useRef(0);
  // Zero point for answer turn timings, set by startAnswerClock and kept across
  // reconnects (unlike sessionStartAtMsRef, which restarts per call).
  const answerTimingOriginMsRef = useRef(0);
  // Stretches with no working call (a call's teardown until the next call's
  // channel opens), left out of turn timings so time in Error, Suspended or
  // reconnecting isn't answer time. callEndedAtMsRef holds the open one's start.
  const offlineGapsRef = useRef<{ fromMs: number; toMs: number }[]>([]);
  const callEndedAtMsRef = useRef<number | null>(null);
  // Azure's own identifier for the live call (from the negotiate response) —
  // needed so teardown's "session ended" beacon can name which call ended.
  // Null whenever there's no active call, or the server couldn't hand one
  // back (see VoiceSessionResponseSchema's callId comment).
  const voiceCallIdRef = useRef<string | null>(null);
  const micStopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Always points at the latest stopAnswering (assigned below, once it's
  // declared). connect() schedules the mic-stop timer above that declaration,
  // so a ref is the only way to reach it.
  const capStopAnsweringRef = useRef<() => void>(() => {});
  // True once the mic-stop timer has fired — i.e. we're inside the reserve,
  // with only enough of the cap left for this answer's feedback. Read in two
  // places: the coach finishing ends the session outright rather than opening
  // a follow-up there's no budget for, and a dropped connection is read as the
  // server's planned hangup rather than a failure.
  const inFeedbackReserveRef = useRef(false);
  // The cap for the live call, so the connection-drop handler can tell a
  // planned hangup from a real failure without waiting on a timer that a
  // backgrounded tab may have throttled. Zero until negotiation returns one.
  const maxSessionDurationMsRef = useRef(0);

  const controlsRef = useRef<{
    answeringPhase: RealtimeControlFrame;
    discussingPhase: RealtimeControlFrame;
    feedbackNudge: RealtimeControlFrame;
    askQuestion: RealtimeControlFrame;
  } | null>(null);

  // Which response.create is in flight, if any — null once the model starts
  // speaking (or the request is abandoned). See RealtimeResponseRequest.
  const pendingResponseRequestRef = useRef<RealtimeResponseRequest | null>(null);
  const responseRequestRetryCountRef = useRef(0);
  const responseRequestRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const askQuestionFallbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Always points at the latest "give up on the question and open the mic"
  // action (assigned below, once stopAsking is declared). Both the backstop
  // timer and the retry path reach it from above that declaration.
  const askQuestionFallbackRef = useRef<() => void>(() => {});
  // True while the mic is muted for a follow-up coach turn, so the coach
  // finishing only undoes that mute, never one made by Pause.
  const micMutedForCoachRef = useRef(false);
  // Set just before the coach-turn unmute re-opens the mic, so that mic-on
  // doesn't start a new transcription-failure count.
  const micResumedAfterCoachRef = useRef(false);
  const finalizeGraceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Submits the answer stopAnswering is waiting to assemble — set from Stop
  // until it runs, so a drop in between can submit it rather than lose it.
  const pendingCaptureRef = useRef<(() => void) | null>(null);
  // Candidate item ids created but not yet transcribed — stopAnswering waits
  // for this to drain before assembling the answer. Seeded from ItemAdded
  // (item creation), not the first delta: transcription lags speech, so a
  // turn with no delta yet at Stop-time would otherwise never be tracked and
  // would just get silently dropped from the answer.
  const pendingCandidateAnswerItemsRef = useRef<Set<string>>(new Set());

  // True while commitInputAudioBuffer's own request is in flight — its round
  // trip has the same gap as above: if neither its outcome (a new item, or an
  // empty-buffer error) nor a delta has landed yet, nothing says "wait."
  // Cleared by the next ItemAdded or Error event.
  const awaitingBufferCommitRef = useRef(false);

  // See armMicEnableOnAck — holds the mic-enable callback while it's waiting
  // on the discussing-phase session.update to be acked.
  const pendingMicEnableRef = useRef<(() => void) | null>(null);
  const micEnableFallbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The single drop handler for whichever connection is live (assigned in
  // connect(), a no-op otherwise) — a ref because sendChannelPayload and the
  // ask-question fallback sit above connect() but must be able to declare the
  // call dead.
  const connectionDropRef = useRef<(event: VoiceClientEvent, detail?: ClientEventDetail) => void>(
    () => {}
  );
  const connectRef = useRef<(options?: { silent?: boolean }) => Promise<void>>(() =>
    Promise.resolve()
  );
  // Set once the one silent retry has been spent; cleared once a retry's call
  // has stayed up for RECONNECT_STABLE_MS, or by any connect the candidate
  // starts themselves.
  const autoReconnectAttemptedRef = useRef(false);
  // True from a drop (or a manual reconnect) until the next connection lands.
  // Drives the reconnected notice, and tells a lost call apart from a first
  // connect that never landed.
  const droppedSinceLastLandingRef = useRef(false);
  const disconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dataChannelOpenTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Client-event reports go out strictly in order, and teardown's end beacon
  // waits on the tail: endSession forgets the call server-side, so a later
  // report would be logged without its assessmentId.
  const reportChainRef = useRef<Promise<void>>(Promise.resolve());
  // Turn writes are chained so a reconnect can await the tail, and the server's
  // conversation-so-far includes everything said before the drop.
  const flushChainRef = useRef<Promise<void>>(Promise.resolve());

  const micOffTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const transcriptionFailuresRef = useRef(0);

  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const analyserBufferRef = useRef<Uint8Array<ArrayBuffer> | null>(null);
  const stoppingRef = useRef(false);
  const sessionGenerationRef = useRef(0);

  // Turns not yet confirmed written to /conversations — flushed debounced,
  // and forced immediately before any teardown so nothing is silently dropped.
  const pendingFlushRef = useRef<ConversationTurn[]>([]);
  const flushTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // A ref, not a dep: onRevoked's identity follows the parent's navigate, and
  // via flushNow → teardown a new one would end the call. Nulled on unmount so
  // a late 410 can't navigate a candidate who has already left.
  const onRevokedRef = useRef<((err: unknown) => void) | null>(null);
  useEffect(() => {
    onRevokedRef.current = onRevoked;
    return () => {
      onRevokedRef.current = null;
    };
  }, [onRevoked]);

  // Resolves once everything handed over so far has been written (or has
  // failed — never rejects), so a reconnect can wait on the transcript it
  // resumes from.
  const flushNow = useCallback((): Promise<void> => {
    if (flushTimeoutRef.current !== null) {
      clearTimeout(flushTimeoutRef.current);
      flushTimeoutRef.current = null;
    }
    const turns = pendingFlushRef.current;
    if (turns.length > 0) {
      pendingFlushRef.current = [];
      flushChainRef.current = flushChainRef.current.then(async () => {
        try {
          const token = await getToken();
          if (!token) return;
          await new ConversationsService(token).appendTurns(
            assessmentId,
            interviewTemplateItemId,
            { turns },
            { signal: AbortSignal.timeout(BACKGROUND_REQUEST_TIMEOUT_MS) }
          );
        } catch (err) {
          // Not best-effort: a 410 means the package was revoked.
          if (isRevokedError(err)) {
            onRevokedRef.current?.(err);
            return;
          }
          // Best-effort — a dropped flush just means this turn is missing from
          // a reconnect's <conversation_so_far> block, not a broken session.
          console.warn('Failed to flush conversation turns', err);
        }
      });
    }
    return flushChainRef.current;
  }, [assessmentId, interviewTemplateItemId, getToken]);

  // Best-effort report of a client-side health event to the server log (see
  // VoiceService.reportEvent). A no-op without a callId — negotiation failures
  // are already logged by the router.
  const reportEvent = useCallback(
    (event: VoiceClientEvent, detail?: ClientEventDetail) => {
      const callId = voiceCallIdRef.current;
      if (!callId) return;
      const clipped = detail && {
        state: clip(detail.state),
        code: clip(detail.code),
        message: clip(detail.message),
      };
      reportChainRef.current = reportChainRef.current
        .then(async () => {
          const token = await getToken();
          if (!token) return;
          await new VoiceService(token).reportEvent(
            { callId, event, detail: clipped },
            { signal: AbortSignal.timeout(BACKGROUND_REQUEST_TIMEOUT_MS) }
          );
        })
        .catch(() => undefined);
    },
    [getToken]
  );

  const scheduleFlush = useCallback(
    (turn: ConversationTurn) => {
      pendingFlushRef.current.push(turn);
      if (flushTimeoutRef.current !== null) clearTimeout(flushTimeoutRef.current);
      flushTimeoutRef.current = setTimeout(flushNow, TURN_FLUSH_DEBOUNCE_MS);
    },
    [flushNow]
  );

  // Rebuilds `entries` from historyEntriesRef's fixed prefix plus itemOrderRef
  // mapped through itemContentRef — the single place the two refs turn into
  // the array everything else reads. Never called with anything that mutates
  // itemOrderRef's existing positions, only its content-map values or its tail.
  const rebuildEntries = useCallback(() => {
    const ordered: ConversationEntry[] = [];
    for (const itemId of itemOrderRef.current) {
      const entry = itemContentRef.current.get(itemId);
      if (entry) ordered.push(entry);
    }
    commitEntries([...historyEntriesRef.current, ...ordered]);
  }, [commitEntries]);

  const cancelPendingResponseRequest = useCallback(() => {
    if (responseRequestRetryTimeoutRef.current !== null) {
      clearTimeout(responseRequestRetryTimeoutRef.current);
      responseRequestRetryTimeoutRef.current = null;
    }
    pendingResponseRequestRef.current = null;
    responseRequestRetryCountRef.current = 0;
    setIsThinking(false);
  }, []);

  const cancelAskQuestionFallback = useCallback(() => {
    if (askQuestionFallbackTimeoutRef.current !== null) {
      clearTimeout(askQuestionFallbackTimeoutRef.current);
      askQuestionFallbackTimeoutRef.current = null;
    }
  }, []);

  const cancelPendingMicEnable = useCallback(() => {
    if (micEnableFallbackTimeoutRef.current !== null) {
      clearTimeout(micEnableFallbackTimeoutRef.current);
      micEnableFallbackTimeoutRef.current = null;
    }
    pendingMicEnableRef.current = null;
  }, []);

  // Runs `enable` once the server acks the discussing-phase session.update
  // just sent (or after SESSION_UPDATE_ACK_FALLBACK_MS, whichever is first) —
  // never before, so the mic can't go hot while create_response is still
  // false from the outgoing profile.
  const armMicEnableOnAck = useCallback(
    (enable: () => void) => {
      cancelPendingMicEnable();
      pendingMicEnableRef.current = enable;
      micEnableFallbackTimeoutRef.current = setTimeout(() => {
        micEnableFallbackTimeoutRef.current = null;
        console.warn('No session.updated ack for discussing-phase switch — enabling mic anyway');
        pendingMicEnableRef.current = null;
        enable();
      }, SESSION_UPDATE_ACK_FALLBACK_MS);
    },
    [cancelPendingMicEnable]
  );

  const setMicEnabled = useCallback((enabled: boolean) => {
    localStreamRef.current?.getTracks().forEach((track) => {
      track.enabled = enabled;
    });
    setMicLive(enabled);
  }, []);

  // Starts the answer clock when the mic first goes live for the answer, so
  // connecting and hearing the question never count. No-op once started.
  const startAnswerClock = useCallback(() => {
    if (answerTimingOriginMsRef.current === 0) answerTimingOriginMsRef.current = Date.now();
  }, []);

  // The one transition out of Asking: the question has finished playing, so
  // the answering window opens and the mic goes live. Guarded on the phase
  // rather than assumed, because the backstop timer and the give-up path can
  // both race the `output_audio_buffer.stopped` that normally gets here first
  // — whichever arrives first wins and the rest are no-ops.
  const stopAsking = useCallback(() => {
    if (phaseRef.current !== VoicePhase.Asking) return;
    cancelAskQuestionFallback();
    // The opening response is abandoned along with the phase, not just left to
    // finish. Still pending, it would hold isThinking true — which outranks
    // micLive in `activity`, pinning the orb on Thinking for the whole answer
    // — and a later realtime error would retry its response.create into the
    // middle of that answer. A no-op on the normal path, where the coach's
    // first transcript delta already cleared it.
    cancelPendingResponseRequest();
    startAnswerClock();
    setPhase(VoicePhase.Answering);
    setMicEnabled(true);
  }, [cancelAskQuestionFallback, cancelPendingResponseRequest, startAnswerClock, setMicEnabled]);

  useEffect(() => {
    askQuestionFallbackRef.current = () => {
      if (phaseRef.current !== VoicePhase.Asking) return;
      // Opening the mic on a channel that can't carry anything would leave it
      // "recording" to nothing — it's a drop, not a fallback.
      const channelState = dataChannelRef.current?.readyState;
      if (channelState !== 'open') {
        console.warn('Opening question stalled with the data channel not open — treating as a dropped connection');
        connectionDropRef.current(
          channelState === 'connecting'
            ? VoiceClientEvent.DataChannelOpenTimeout
            : VoiceClientEvent.DataChannelClosed
        );
        return;
      }
      console.warn('Opening question stalled — opening the mic anyway');
      stopAsking();
    };
  }, [stopAsking]);

  const armAskQuestionFallback = useCallback(
    (delayMs: number) => {
      cancelAskQuestionFallback();
      askQuestionFallbackTimeoutRef.current = setTimeout(() => {
        askQuestionFallbackTimeoutRef.current = null;
        askQuestionFallbackRef.current();
      }, delayMs);
    },
    [cancelAskQuestionFallback]
  );

  // No-op if Pause or teardown already cleared the mute; the mic stays off
  // outside Conversing.
  const unmuteMicAfterCoach = useCallback(() => {
    if (!micMutedForCoachRef.current) return;
    micMutedForCoachRef.current = false;
    if (phaseRef.current === VoicePhase.Conversing) {
      micResumedAfterCoachRef.current = true;
      setMicEnabled(true);
    }
  }, [setMicEnabled]);

  // Every other phase the coach speaks in already has the mic off. Cancels an
  // enable still waiting on the discussingPhase ack so it can't open the mic
  // mid-turn; unmuteMicAfterCoach opens it instead.
  const muteMicForCoach = useCallback(() => {
    if (phaseRef.current !== VoicePhase.Conversing || micMutedForCoachRef.current) return;
    cancelPendingMicEnable();
    setMicEnabled(false);
    micMutedForCoachRef.current = true;
  }, [cancelPendingMicEnable, setMicEnabled]);

  const teardown = useCallback(() => {
    stoppingRef.current = true;
    sessionGenerationRef.current += 1;
    cancelPendingResponseRequest();
    cancelPendingMicEnable();
    cancelAskQuestionFallback();
    micMutedForCoachRef.current = false;
    micResumedAfterCoachRef.current = false;
    // Both describe the call being torn down, so a reconnect starts clean
    // rather than inheriting an endgame that ended the moment its coach first
    // stopped speaking.
    inFeedbackReserveRef.current = false;
    maxSessionDurationMsRef.current = 0;
    connectionDropRef.current = () => {};
    if (micOffTimeoutRef.current !== null) {
      clearTimeout(micOffTimeoutRef.current);
      micOffTimeoutRef.current = null;
    }
    setMicTrackIssue(null);
    if (disconnectTimeoutRef.current !== null) {
      clearTimeout(disconnectTimeoutRef.current);
      disconnectTimeoutRef.current = null;
    }
    if (dataChannelOpenTimeoutRef.current !== null) {
      clearTimeout(dataChannelOpenTimeoutRef.current);
      dataChannelOpenTimeoutRef.current = null;
    }
    // Partial turns from this call will never finish. They're discarded, not
    // flushed, so a reconnect resumes from the last finished turn (which the
    // reconnect notice quotes). Their pending ids go too, or the next Stop
    // would wait MAX_FINALIZE_WAIT_MS on them.
    let discardedPartial = false;
    for (const [itemId, entry] of itemContentRef.current) {
      if (entry.role === ConversationRole.Candidate && entry.isStreaming) {
        itemContentRef.current.delete(itemId);
        discardedPartial = true;
      }
    }
    if (discardedPartial) rebuildEntries();
    pendingCandidateAnswerItemsRef.current.clear();
    awaitingBufferCommitRef.current = false;
    setMicLive(false);
    setIsCoachSpeaking(false);
    setIsThinking(false);
    if (finalizeGraceTimeoutRef.current !== null) {
      clearTimeout(finalizeGraceTimeoutRef.current);
      finalizeGraceTimeoutRef.current = null;
    }
    pendingCaptureRef.current = null;
    if (micStopTimeoutRef.current !== null) {
      clearTimeout(micStopTimeoutRef.current);
      micStopTimeoutRef.current = null;
    }
    void flushNow();

    // "This call ended" beacon — lets the backend close the voice
    // observability span promptly rather than leaving it open until its own
    // length-cap timer fires. Never blocks teardown itself (VoiceService
    // .endSession already swallows its own errors). Sent after any health
    // report already queued for this call, so those are logged while the
    // server still tracks it.
    const endedCallId = voiceCallIdRef.current;
    if (endedCallId) {
      voiceCallIdRef.current = null;
      const reportsSettled = reportChainRef.current;
      void (async () => {
        await reportsSettled;
        const token = await getToken();
        if (!token) return;
        await new VoiceService(token).endSession(endedCallId);
      })();
    }

    dataChannelRef.current?.close();
    dataChannelRef.current = null;

    localStreamRef.current?.getTracks().forEach((track) => track.stop());
    localStreamRef.current = null;

    if (
      peerConnectionRef.current &&
      answerTimingOriginMsRef.current !== 0 &&
      callEndedAtMsRef.current === null
    ) {
      callEndedAtMsRef.current = Date.now();
    }
    peerConnectionRef.current?.close();
    peerConnectionRef.current = null;

    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
      remoteAudioRef.current = null;
    }

    if (audioContextRef.current) {
      void audioContextRef.current
        .close()
        .catch((err) => console.warn('Failed to close audio context', err));
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    analyserBufferRef.current = null;

    controlsRef.current = null;
  }, [
    cancelPendingResponseRequest,
    cancelPendingMicEnable,
    cancelAskQuestionFallback,
    flushNow,
    getToken,
    rebuildEntries,
  ]);

  const getRemoteAudioLevel = useCallback((): number => {
    const analyser = analyserRef.current;
    const buffer = analyserBufferRef.current;
    if (!analyser || !buffer) return 0;
    analyser.getByteTimeDomainData(buffer);
    let sumSquares = 0;
    for (let i = 0; i < buffer.length; i++) {
      const normalized = (buffer[i] - 128) / 128;
      sumSquares += normalized * normalized;
    }
    const rms = Math.sqrt(sumSquares / buffer.length);
    return Math.min(1, rms * REMOTE_AUDIO_LEVEL_GAIN);
  }, []);

  const fail = useCallback(
    (message: string) => {
      teardown();
      setPhase(VoicePhase.Error);
      setErrorMessage(message);
    },
    [teardown]
  );

  // Full teardown on unmount — a mode switch away from voice unmounts this
  // hook's owner (VoiceAnswerPanel), which is exactly what "Suspended" means:
  // connection torn down, transcript already mirrored into the store via
  // onEntriesChange, nothing lost. Re-mirrored here because teardown's
  // discarded partials won't trigger another render.
  const onEntriesChangeRef = useRef(onEntriesChange);
  onEntriesChangeRef.current = onEntriesChange;
  useEffect(
    () => () => {
      teardown();
      onEntriesChangeRef.current(entriesRef.current);
    },
    [teardown]
  );

  // The channel's readyState only flips to 'open' asynchronously once the
  // underlying SCTP/DTLS handshake finishes — negotiation resolving (offer/
  // answer exchanged) doesn't mean it's ready yet, so a send right after
  // connect() can still race a channel stuck in 'connecting'.
  const sendChannelPayload = useCallback((payload: unknown) => {
    const channel = dataChannelRef.current;
    if (!channel) return;
    if (channel.readyState === 'open') {
      channel.send(JSON.stringify(payload));
    } else if (channel.readyState === 'connecting') {
      channel.addEventListener(
        'open',
        () => {
          if (channel.readyState === 'open') channel.send(JSON.stringify(payload));
        },
        { once: true }
      );
    } else {
      // closing/closed: the payload can never arrive, so this is a drop.
      // Deferred so the click that sent it (Stop, Resume, Pause) sets its phase
      // first — inline, the drop would be judged against the pre-click phase.
      console.warn(`[voice] send on a ${channel.readyState} data channel — treating as a dropped connection`);
      const drop = connectionDropRef.current;
      setTimeout(() => drop(VoiceClientEvent.DataChannelClosed), 0);
    }
  }, []);

  // Runs `fn` once the live data channel is open — now if it already is. Never
  // runs it for a channel that has since been replaced or closed, so whatever
  // is gated on it (the mic going live) can't outlive the call.
  const whenChannelOpen = useCallback((fn: () => void) => {
    const channel = dataChannelRef.current;
    if (!channel) return;
    if (channel.readyState === 'open') {
      fn();
      return;
    }
    channel.addEventListener(
      'open',
      () => {
        if (dataChannelRef.current === channel) fn();
      },
      { once: true }
    );
  }, []);

  const sendControlFrame = useCallback(
    (frame: RealtimeControlFrame) => sendChannelPayload(frame),
    [sendChannelPayload]
  );

  // Forces whatever's still buffered to finalize into an item right now,
  // instead of waiting on semantic_vad's own end-of-turn confirmation (which
  // buildAudioInput notes has a "noticeable" delay). Sent the moment Stop is
  // clicked, so speech with no pause after it still becomes a trackable item.
  // Carries no content, unlike the server-authored frames above, so it's sent
  // as a plain client event rather than through RealtimeControlFrameSchema.
  const commitInputAudioBuffer = useCallback(() => {
    awaitingBufferCommitRef.current = true;
    sendChannelPayload({ type: RealtimeOutboundEventType.InputAudioBufferCommit });
  }, [sendChannelPayload]);

  // Adds application context the coach reads without treating it as the
  // candidate speaking (a user item would be) or replying to it. Its
  // conversation.item.added carries no transcript, so it never becomes an
  // entry, a flushed turn, or something Stop waits on (see ItemAdded below).
  const sendSystemItem = useCallback(
    (text: string) => {
      sendChannelPayload({
        type: RealtimeOutboundEventType.ConversationItemCreate,
        item: { type: 'message', role: 'system', content: [{ type: 'input_text', text }] },
      });
    },
    [sendChannelPayload]
  );

  const responseRequestFrame = useCallback(
    (request: RealtimeResponseRequest): RealtimeControlFrame | undefined =>
      request === RealtimeResponseRequest.AskQuestion
        ? controlsRef.current?.askQuestion
        : controlsRef.current?.feedbackNudge,
    []
  );

  const sendResponseRequest = useCallback(
    (request: RealtimeResponseRequest) => {
      const frame = responseRequestFrame(request);
      if (!frame) return;
      pendingResponseRequestRef.current = request;
      responseRequestRetryCountRef.current = 0;
      setIsThinking(true);
      sendControlFrame(frame);
    },
    [sendControlFrame, responseRequestFrame]
  );

  const retryResponseRequest = useCallback(() => {
    const generation = sessionGenerationRef.current;
    const request = pendingResponseRequestRef.current;
    if (!request) return;
    if (responseRequestRetryCountRef.current >= MAX_RESPONSE_REQUEST_RETRIES) {
      console.error(`Giving up on ${request} response request after repeated realtime errors`);
      cancelPendingResponseRequest();
      // A feedback turn that never comes leaves the answer already captured and
      // the session usable. An opening question that never comes leaves the mic
      // muted with nothing else to unmute it, so fall straight through to
      // Answering rather than waiting out the backstop in silence.
      if (request === RealtimeResponseRequest.AskQuestion) askQuestionFallbackRef.current();
      return;
    }
    responseRequestRetryCountRef.current += 1;
    responseRequestRetryTimeoutRef.current = setTimeout(() => {
      responseRequestRetryTimeoutRef.current = null;
      if (sessionGenerationRef.current !== generation) return;
      if (pendingResponseRequestRef.current !== request) return;
      const frame = responseRequestFrame(request);
      if (!frame) return;
      console.warn(`Retrying ${request} response request after realtime error`);
      sendControlFrame(frame);
    }, RESPONSE_REQUEST_RETRY_DELAY_MS);
  }, [sendControlFrame, responseRequestFrame, cancelPendingResponseRequest]);

  // Appends on first sight, which reproduces true creation order exactly
  // because conversation.item.added only ever fires in that order. Works even
  // when a delta beats its ItemAdded frame — first sight is still first sight.
  const ensureOrdered = useCallback((itemId: string) => {
    if (!itemOrderRef.current.includes(itemId)) itemOrderRef.current.push(itemId);
  }, []);

  // Appends a delta onto the in-progress entry for this item, starting one if
  // this is its first delta — candidate and coach turns interleave in true
  // occurrence order because each item keeps the array position it was first
  // seen at, never one computed from role or from when it happens to finish.
  const appendLiveDelta = useCallback(
    (role: ConversationRole, delta: string, itemId: string) => {
      ensureOrdered(itemId);
      const existing = itemContentRef.current.get(itemId);
      const kind = existing?.kind ?? kindForPhase(phaseRef.current, role);
      itemContentRef.current.set(itemId, {
        role,
        content: (existing?.content ?? '') + delta,
        isStreaming: true,
        kind,
      });
      if (role === ConversationRole.Candidate && kind === ConversationEntryKind.Answer) {
        pendingCandidateAnswerItemsRef.current.add(itemId);
      }
      rebuildEntries();
    },
    [ensureOrdered, rebuildEntries]
  );

  const finalizeEntry = useCallback(
    (role: ConversationRole, text: string, itemId: string) => {
      ensureOrdered(itemId);
      const existing = itemContentRef.current.get(itemId);
      const kind = existing?.kind ?? kindForPhase(phaseRef.current, role);
      const occurredAtMs = Date.now();
      const clientTurnId = crypto.randomUUID();
      const finalized: ConversationEntry = {
        role,
        content: text,
        isStreaming: false,
        kind,
        clientTurnId,
        occurredAtMs,
      };
      itemContentRef.current.set(itemId, finalized);
      pendingCandidateAnswerItemsRef.current.delete(itemId);
      rebuildEntries();
      // The candidate is being heard again, so the notice no longer applies.
      if (role === ConversationRole.Candidate) setNotice(null);

      // Same occurredAtMs the entry itself carries, so the persisted turn
      // sorts exactly where the local transcript shows it.
      scheduleFlush({ clientTurnId, role, text, occurredAt: new Date(occurredAtMs) });
    },
    [scheduleFlush, ensureOrdered, rebuildEntries]
  );

  // Elapsed since answerTimingOriginMsRef, less every offline stretch that
  // ended before atMs — the clock answer turn timings are measured on.
  const elapsedOnlineMs = useCallback(
    (atMs: number) =>
      offlineGapsRef.current.reduce(
        (ms, gap) => (gap.toMs <= atMs ? ms - (gap.toMs - gap.fromMs) : ms),
        atMs - answerTimingOriginMsRef.current
      ),
    []
  );

  // stoppedAtMs caps every turn's end: a turn still being transcribed at Stop
  // finalizes later, and that wait isn't speaking time.
  const assembleAnswer = useCallback((stoppedAtMs?: number): { transcript: string; turnTimings: VoiceTurnTiming[] } => {
    const answerEntries = entriesRef.current.filter(
      (e) =>
        e.kind === ConversationEntryKind.Answer &&
        e.role === ConversationRole.Candidate &&
        !e.isStreaming
    );
    const transcript = answerEntries.map((e) => e.content).join(VOICE_TURN_SEPARATOR);
    const originMs = answerTimingOriginMsRef.current;
    const capMs = stoppedAtMs != null ? elapsedOnlineMs(stoppedAtMs) : Infinity;
    let previousEndedAtMs = 0;
    const turnTimings: VoiceTurnTiming[] = answerEntries.map((e) => {
      const endedAtMs = Math.min(
        Math.max(elapsedOnlineMs(e.occurredAtMs ?? originMs), previousEndedAtMs),
        capMs
      );
      const startedAtMs = previousEndedAtMs;
      previousEndedAtMs = endedAtMs;
      return { text: e.content, startedAtMs, endedAtMs };
    });
    return { transcript, turnTimings };
  }, [elapsedOnlineMs]);

  const handleRealtimeEvent = useCallback(
    (event: RealtimeEvent) => {
      switch (event.type) {
        case RealtimeEventType.CandidateTranscriptDelta:
          appendLiveDelta(ConversationRole.Candidate, event.delta, event.itemId);
          break;
        case RealtimeEventType.CandidateTranscriptDone:
          finalizeEntry(ConversationRole.Candidate, event.transcript, event.itemId);
          break;
        case RealtimeEventType.CandidateTranscriptFailed:
          // Nothing will ever complete this item, so stop Stop from waiting on
          // it, and drop whatever partial it had streamed in.
          pendingCandidateAnswerItemsRef.current.delete(event.itemId);
          itemContentRef.current.delete(event.itemId);
          rebuildEntries();
          reportEvent(VoiceClientEvent.TranscriptionFailed, {
            code: event.code ?? undefined,
            message: event.message ?? undefined,
          });
          transcriptionFailuresRef.current += 1;
          if (
            transcriptionFailuresRef.current >= TRANSCRIPTION_FAILURE_NOTICE_THRESHOLD &&
            RECORDING_PHASES.has(phaseRef.current)
          ) {
            setNotice(TRANSCRIPTION_FAILED_NOTICE);
          }
          break;
        case RealtimeEventType.CoachTranscriptDelta:
          if (pendingResponseRequestRef.current) cancelPendingResponseRequest();
          if (phaseRef.current === VoicePhase.Finalizing) setPhase(VoicePhase.Feedback);
          setIsCoachSpeaking(true);
          // The opening question never becomes a transcript entry. It is
          // already on screen in the question card, so a turn for it would
          // just duplicate it — and as a leading coach entry it would break
          // FeedbackThread's answer/feedback split and read as a coach reply
          // to the rest of the app. Only the speaking state is taken from it.
          if (phaseRef.current === VoicePhase.Asking) {
            // Proof the question is being read: the start backstop has done
            // its job, and nothing should now bound how long the question
            // takes. OutputAudioStopped ends the phase from here.
            cancelAskQuestionFallback();
            break;
          }
          muteMicForCoach();
          appendLiveDelta(ConversationRole.Coach, event.delta, event.itemId);
          break;
        case RealtimeEventType.CoachTranscriptDone:
          if (pendingResponseRequestRef.current) cancelPendingResponseRequest();
          if (phaseRef.current === VoicePhase.Asking) {
            // Text complete, audio still draining. What remains is bounded, so
            // a dropped OutputAudioStopped can be recovered from without ever
            // having capped the question's own length.
            armAskQuestionFallback(ASK_QUESTION_TAIL_TIMEOUT_MS);
            break;
          }
          finalizeEntry(ConversationRole.Coach, event.transcript, event.itemId);
          break;
        case RealtimeEventType.OutputAudioStopped:
          // Two phases end on the coach falling silent: Asking (the question
          // has been read, so the mic opens) and the spoken feedback that
          // follows Stop. Later Discussing-phase coach turns are just ordinary
          // conversation and don't touch phase.
          if (phaseRef.current === VoicePhase.Asking) {
            stopAsking();
          } else if (phaseRef.current === VoicePhase.Feedback || phaseRef.current === VoicePhase.Finalizing) {
            setPhase(VoicePhase.AnswerCaptured);
          }
          unmuteMicAfterCoach();
          setIsCoachSpeaking(false);
          break;
        case RealtimeEventType.Error:
          console.warn('Realtime API error event received', { code: event.code, message: event.message });
          // Stop commits the buffer even when VAD already did, so an empty
          // commit is routine — not worth a server log line per answer.
          if (event.code !== REALTIME_EMPTY_COMMIT_CODE) {
            reportEvent(VoiceClientEvent.RealtimeError, {
              code: event.code ?? undefined,
              message: event.message ?? undefined,
            });
          }
          if (pendingResponseRequestRef.current) retryResponseRequest();
          // Covers commitInputAudioBuffer's empty-buffer outcome.
          awaitingBufferCommitRef.current = false;
          break;
        case RealtimeEventType.ItemAdded:
          ensureOrdered(event.itemId);
          // Marks it pending before any delta arrives — finalizeEntry clears
          // it once the transcript does. A system item (sendSystemItem) stops
          // at ensureOrdered: with no content it never becomes an entry.
          if (event.isCandidateItem) {
            pendingCandidateAnswerItemsRef.current.add(event.itemId);
            awaitingBufferCommitRef.current = false;
          }
          break;
        case RealtimeEventType.SessionUpdated:
          if (micEnableFallbackTimeoutRef.current !== null) {
            clearTimeout(micEnableFallbackTimeoutRef.current);
            micEnableFallbackTimeoutRef.current = null;
          }
          pendingMicEnableRef.current?.();
          pendingMicEnableRef.current = null;
          break;
      }
    },
    [
      appendLiveDelta,
      finalizeEntry,
      ensureOrdered,
      cancelPendingResponseRequest,
      retryResponseRequest,
      stopAsking,
      cancelAskQuestionFallback,
      armAskQuestionFallback,
      muteMicForCoach,
      unmuteMicAfterCoach,
      rebuildEntries,
      reportEvent,
    ]
  );

  // `silent` is the one automatic retry after a dropped call: same negotiation,
  // but the phase reads Reconnecting and a failure is reported as a lost
  // connection rather than whatever the negotiation happened to throw.
  const connect = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    teardown();
    sessionStartAtMsRef.current = Date.now();
    const generation = ++sessionGenerationRef.current;
    const isCurrentSession = () => sessionGenerationRef.current === generation;
    stoppingRef.current = false;
    // A connect the candidate started gets a fresh retry; the silent one is the retry.
    if (!silent) autoReconnectAttemptedRef.current = false;
    setErrorMessage(null);
    setNotice(null);
    setPhase(silent ? VoicePhase.Reconnecting : VoicePhase.Connecting);

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      if (isCurrentSession()) fail(micErrorMessage(err));
      return;
    }

    if (!isCurrentSession()) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    localStreamRef.current = stream;

    // The browser's own word on whether the mic is delivering anything: 'mute'
    // when the OS or hardware mutes it, 'ended' when the device goes away. Our
    // own muting (track.enabled) fires neither. Only a track that *stays* off
    // counts, since a new one starts out muted until its first audio.
    const micTrack = stream.getAudioTracks()[0];
    if (micTrack) {
      const currentIssue = (): MicIssue | null =>
        micTrack.readyState === 'ended' ? MicIssue.Disconnected : micTrack.muted ? MicIssue.Muted : null;
      const syncMicOff = () => {
        if (micOffTimeoutRef.current !== null) {
          clearTimeout(micOffTimeoutRef.current);
          micOffTimeoutRef.current = null;
        }
        if (currentIssue() === null) {
          setMicTrackIssue(null);
          return;
        }
        micOffTimeoutRef.current = setTimeout(() => {
          micOffTimeoutRef.current = null;
          if (!isCurrentSession()) return;
          const issue = currentIssue();
          setMicTrackIssue(issue);
          if (issue) reportEvent(VoiceClientEvent.MicOff, { state: issue });
        }, MIC_OFF_GRACE_MS);
      };
      micTrack.onmute = syncMicOff;
      micTrack.onunmute = syncMicOff;
      micTrack.onended = syncMicOff;
      syncMicOff();
    }

    try {
      const peerConnection = new RTCPeerConnection();
      peerConnectionRef.current = peerConnection;

      stream.getTracks().forEach((track) => peerConnection.addTrack(track, stream));

      const remoteAudio = new Audio();
      remoteAudio.autoplay = true;
      remoteAudioRef.current = remoteAudio;
      peerConnection.ontrack = (event) => {
        const [stream] = event.streams;
        remoteAudio.srcObject = stream ?? null;
        if (!stream) return;

        try {
          const audioContext = new AudioContext();
          const source = audioContext.createMediaStreamSource(stream);
          const analyser = audioContext.createAnalyser();
          analyser.fftSize = REMOTE_AUDIO_FFT_SIZE;
          analyser.smoothingTimeConstant = 0;
          const silentGain = audioContext.createGain();
          silentGain.gain.value = 0;
          source.connect(analyser);
          analyser.connect(silentGain);
          silentGain.connect(audioContext.destination);

          audioContextRef.current = audioContext;
          analyserRef.current = analyser;
          analyserBufferRef.current = new Uint8Array(analyser.fftSize);
        } catch (err) {
          console.warn('Failed to set up remote audio analysis', err);
        }
      };

      const isCurrentConnection = () =>
        !stoppingRef.current && peerConnectionRef.current === peerConnection;

      // A drop at the cap is the server hanging the call up, not a network
      // failure — end it the way a mode switch does (transcript kept,
      // reconnect available) instead of reporting an error for something that
      // worked as designed. Measured against the clock rather than off
      // inFeedbackReserveRef, so a backgrounded tab that never ran the
      // mic-stop timer still reads its own hangup correctly. Anything earlier
      // than the cap is a real failure and still surfaces as one: the reserve
      // is when the *mic* stops, not when the server hangs up, so testing
      // against it would silently swallow a genuine mid-session drop.
      const droppedOnLengthCap = () =>
        maxSessionDurationMsRef.current > 0 &&
        Date.now() - sessionStartAtMsRef.current >=
          maxSessionDurationMsRef.current - LENGTH_CAP_DROP_TOLERANCE_MS;

      // Every way this hook learns the call is dead ends up here: peer/ICE
      // state, the data channel closing or never opening, or a send on a
      // closed channel.
      const handleConnectionDrop = (event: VoiceClientEvent, detail?: ClientEventDetail) => {
        if (!isCurrentConnection()) return;
        // Evaluate is plain HTTP, so a pending Stop can still submit — but only
        // a complete answer. With a turn still transcribing or the commit
        // unacked it would be cut off (often empty: semantic_vad makes a long
        // answer one turn) and couldn't be redone; instead teardown drops the
        // capture and the candidate reconnects into Answering to finish.
        const answerComplete =
          pendingCandidateAnswerItemsRef.current.size === 0 && !awaitingBufferCommitRef.current;
        if (answerComplete) pendingCaptureRef.current?.();
        // Only Stop leads into these phases, and it holds a pending capture
        // until the answer is submitted — so none left means it was.
        const answerSubmitted =
          (phaseRef.current === VoicePhase.Finalizing || phaseRef.current === VoicePhase.Feedback) &&
          pendingCaptureRef.current === null;
        const lostMessage = answerSubmitted ? CONNECTION_LOST_SUBMITTED_MESSAGE : CONNECTION_LOST_MESSAGE;
        if (droppedOnLengthCap()) {
          teardown();
          setPhase(VoicePhase.Suspended);
          return;
        }
        // A first connect that never landed: nothing was lost, so it fails the
        // way any other connect failure does ("Retry", no reconnected notice).
        if (phaseRef.current === VoicePhase.Connecting && !droppedSinceLastLandingRef.current) {
          reportEvent(event, detail);
          fail(CONNECT_TIMEOUT_MESSAGE);
          return;
        }
        droppedSinceLastLandingRef.current = true;
        // Before anything below tears the call down, so teardown's end beacon
        // queues behind this report.
        reportEvent(event, detail);
        // Idle with the answer submitted: nothing was lost, so end quietly, as
        // the length cap does, rather than raise an alarm. A pending capture
        // means feedback ended before the answer was submitted.
        if (CONNECTED_IDLE_PHASES.has(phaseRef.current) && pendingCaptureRef.current === null) {
          teardown();
          setPhase(VoicePhase.Suspended);
          return;
        }
        if (autoReconnectAttemptedRef.current) {
          reportEvent(VoiceClientEvent.ReconnectFailed);
          fail(lostMessage);
          return;
        }
        if (!AUTO_RECONNECT_PHASES.has(phaseRef.current)) {
          fail(lostMessage);
          return;
        }
        autoReconnectAttemptedRef.current = true;
        void connectRef.current({ silent: true });
      };
      connectionDropRef.current = handleConnectionDrop;

      // 'disconnected' is often transient, so it only counts after a grace
      // period; the timer follows the *pair* of states, and clears the moment
      // neither is disconnected.
      const syncDisconnectTimer = () => {
        const peerDisconnected = peerConnection.connectionState === 'disconnected';
        if (!peerDisconnected && peerConnection.iceConnectionState !== 'disconnected') {
          if (disconnectTimeoutRef.current !== null) {
            clearTimeout(disconnectTimeoutRef.current);
            disconnectTimeoutRef.current = null;
          }
          return;
        }
        if (disconnectTimeoutRef.current !== null) return;
        disconnectTimeoutRef.current = setTimeout(() => {
          disconnectTimeoutRef.current = null;
          if (!isCurrentConnection()) return;
          handleConnectionDrop(
            peerConnection.connectionState === 'disconnected'
              ? VoiceClientEvent.PeerState
              : VoiceClientEvent.IceState,
            { state: 'disconnected' }
          );
        }, DISCONNECT_GRACE_MS);
      };

      peerConnection.onconnectionstatechange = () => {
        if (!isCurrentConnection()) return;
        const state = peerConnection.connectionState;
        if (TERMINAL_CONNECTION_STATES.includes(state)) {
          handleConnectionDrop(VoiceClientEvent.PeerState, { state });
          return;
        }
        syncDisconnectTimer();
      };
      peerConnection.oniceconnectionstatechange = () => {
        if (!isCurrentConnection()) return;
        const state = peerConnection.iceConnectionState;
        if (TERMINAL_ICE_STATES.includes(state)) {
          handleConnectionDrop(VoiceClientEvent.IceState, { state });
          return;
        }
        syncDisconnectTimer();
      };

      const dataChannel = peerConnection.createDataChannel(DATA_CHANNEL_LABEL);
      dataChannelRef.current = dataChannel;
      dataChannel.onopen = () => {
        if (dataChannelOpenTimeoutRef.current !== null) {
          clearTimeout(dataChannelOpenTimeoutRef.current);
          dataChannelOpenTimeoutRef.current = null;
        }
        if (isCurrentConnection() && callEndedAtMsRef.current !== null) {
          offlineGapsRef.current.push({ fromMs: callEndedAtMsRef.current, toMs: Date.now() });
          callEndedAtMsRef.current = null;
        }
        // A retry that stays up earns the next drop its own retry. Not on open:
        // a call that dies right after opening would then retry forever.
        if (silent) {
          setTimeout(() => {
            if (isCurrentConnection()) autoReconnectAttemptedRef.current = false;
          }, RECONNECT_STABLE_MS);
        }
      };
      // Not on a teardown's own close (isCurrentConnection checks stoppingRef).
      dataChannel.onclose = () => {
        if (!isCurrentConnection()) return;
        handleConnectionDrop(VoiceClientEvent.DataChannelClosed);
      };
      dataChannel.onerror = (event) => console.warn('[voice] data channel error', event);
      dataChannel.onmessage = (event) => {
        if (typeof event.data !== 'string') return;
        const realtimeEvent = parseRealtimeEvent(event.data);
        if (realtimeEvent) handleRealtimeEvent(realtimeEvent);
      };

      const offer = await peerConnection.createOffer();
      await peerConnection.setLocalDescription(offer);

      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      if (!offer.sdp) throw new Error('Failed to create local SDP offer');

      // The server picks the phase to resume in from the transcript it has
      // stored, so the previous call's last turns must have landed first —
      // otherwise a mid-answer reconnect can restart in Asking and read the
      // question out again. (With nothing saved, starting over is expected.)
      await waitAtMost(flushNow(), RECONNECT_FLUSH_WAIT_MS);
      if (!isCurrentSession()) return;

      const voiceService = new VoiceService(token);
      const { answerSdp, initialPhase, controls, callId, maxSessionDurationMs } =
        await voiceService.negotiateSession(
          { assessmentId, interviewTemplateItemId, offerSdp: offer.sdp },
          { signal: AbortSignal.timeout(NEGOTIATE_TIMEOUT_MS) }
        );

      if (!isCurrentSession()) return;
      await peerConnection.setRemoteDescription({ type: 'answer', sdp: answerSdp });
      controlsRef.current = controls;

      // Armed here rather than at createDataChannel: the channel can't open
      // before the answer is applied, so counting negotiation time against it
      // would flag slow negotiations as dead channels.
      if (dataChannel.readyState !== 'open') {
        dataChannelOpenTimeoutRef.current = setTimeout(() => {
          dataChannelOpenTimeoutRef.current = null;
          if (!isCurrentConnection() || dataChannel.readyState === 'open') return;
          handleConnectionDrop(VoiceClientEvent.DataChannelOpenTimeout);
        }, DATA_CHANNEL_OPEN_TIMEOUT_MS);
      }

      // No callId means the server couldn't parse Azure's call identifier (see
      // VoiceSessionResponseSchema). The call still works; this connection just
      // gets no end-of-session beacon and no server-side cap, so there's no cap
      // for the mic-stop timer to reserve against either.
      voiceCallIdRef.current = callId ?? null;
      if (silent) reportEvent(VoiceClientEvent.AutoReconnect);
      if (callId) {
        maxSessionDurationMsRef.current = maxSessionDurationMs;
        if (micStopTimeoutRef.current !== null) clearTimeout(micStopTimeoutRef.current);
        // Stopping the mic is the only thing the cap can't be trusted to leave
        // time for: an answer still being spoken when the connection dies is
        // lost, where feedback and follow-up are merely cut short. Everything
        // else runs its natural course inside the remaining budget.
        //
        // Floored at half the cap so a cap shorter than the reserve (a local
        // test value) still leaves a usable answering window instead of muting
        // the mic on connect.
        micStopTimeoutRef.current = setTimeout(() => {
          micStopTimeoutRef.current = null;
          inFeedbackReserveRef.current = true;
          capStopAnsweringRef.current();
        }, Math.max(
          maxSessionDurationMs - VOICE_FEEDBACK_RESERVE_MS,
          maxSessionDurationMs * MIN_ANSWERING_SHARE_OF_CAP,
        ));
      }

      if (initialPhase === VoiceSessionPhase.Asking && controls.askQuestion) {
        // A fresh attempt: the coach reads the question before the candidate
        // answers. The session was minted with the Answering profile, which
        // Asking runs under unchanged, so nothing is being switched and there
        // is no session.updated ack to wait on — unlike the discussing branch
        // below. The mic stays muted until the question has finished playing
        // (OutputAudioStopped → stopAsking), so the coach can't hear itself
        // and the candidate can't answer over it.
        //
        // The frame is checked rather than assumed: the response is typed, not
        // parsed, so a server that named this phase without sending the frame
        // (a version skew mid-deploy) would otherwise mute the mic with nothing
        // to send. Falling through to the plain Answering branch below costs
        // the spoken question and nothing else.
        //
        // Held until the channel is open, like the branches below, so a call
        // that never opens fails from Connecting rather than mid-question.
        setMicEnabled(false);
        whenChannelOpen(() => {
          if (!isCurrentSession()) return;
          droppedSinceLastLandingRef.current = false;
          setPhase(VoicePhase.Asking);
          armAskQuestionFallback(ASK_QUESTION_START_TIMEOUT_MS);
          sendResponseRequest(RealtimeResponseRequest.AskQuestion);
        });
      } else if (initialPhase === VoiceSessionPhase.Discussing && coachingEnabled) {
        // Reconnecting into an already-answered question — the click that
        // triggered connect() already expressed intent to talk, so go
        // straight into the live follow-up conversation rather than landing
        // in a muted waiting state that would need a second click.
        // A fresh getUserMedia stream's tracks are live by default, so mute
        // first — armMicEnableOnAck re-enables once discussingPhase is acked.
        //
        // Held until the channel is open, like the Answering branch below: the
        // discussingPhase frame and the mic both assume a channel that works.
        // The phase stays Connecting/Reconnecting until then.
        droppedSinceLastLandingRef.current = false;
        setMicEnabled(false);
        whenChannelOpen(() => {
          if (!isCurrentSession()) return;
          armMicEnableOnAck(() => setMicEnabled(true));
          sendControlFrame(controls.discussingPhase);
          setPhase(VoicePhase.Conversing);
        });
      } else if (initialPhase === VoiceSessionPhase.Discussing) {
        // Already answered, but coaching is disabled for this package — land
        // silently in AnswerCaptured, never open a live conversation channel.
        droppedSinceLastLandingRef.current = false;
        setMicEnabled(false);
        setPhase(VoicePhase.AnswerCaptured);
      } else {
        // The mic goes live only once the channel is open, so it never
        // "records" to a dead call. Until then the phase stays
        // Connecting/Reconnecting and the fresh track is muted, so early speech
        // isn't filed under the wrong phase.
        setMicEnabled(false);
        whenChannelOpen(() => {
          if (!isCurrentSession()) return;
          startAnswerClock();
          setMicEnabled(true);
          setPhase(VoicePhase.Answering);
          if (droppedSinceLastLandingRef.current) setNotice(droppedNotice(assembleAnswer().transcript));
          droppedSinceLastLandingRef.current = false;
        });
      }
    } catch (err) {
      if (!isCurrentSession()) return;
      if (isRevokedError(err)) {
        // Release the mic and connection now rather than waiting for the kick-out to unmount us.
        teardown();
        setPhase(VoicePhase.Idle);
        onRevokedRef.current?.(err);
        return;
      }
      if (silent) {
        // After negotiation this still has the retry's callId; before it,
        // there's nothing to send and the router's own log covers it.
        reportEvent(VoiceClientEvent.ReconnectFailed, { message: BaseAPIClient.parseErrorMsg(err) });
        fail(CONNECTION_LOST_MESSAGE);
        return;
      }
      const timedOut = err instanceof DOMException && err.name === 'TimeoutError';
      fail(timedOut ? CONNECT_TIMEOUT_MESSAGE : BaseAPIClient.parseErrorMsg(err));
    }
  }, [
    assessmentId,
    interviewTemplateItemId,
    coachingEnabled,
    getToken,
    handleRealtimeEvent,
    teardown,
    fail,
    flushNow,
    reportEvent,
    whenChannelOpen,
    assembleAnswer,
    sendControlFrame,
    sendResponseRequest,
    armMicEnableOnAck,
    armAskQuestionFallback,
    startAnswerClock,
    setMicEnabled,
  ]);
  connectRef.current = connect;

  const reconnect = useCallback(() => {
    droppedSinceLastLandingRef.current = true;
    void connect();
  }, [connect]);

  // Transcription failures are counted per recording window. A follow-up
  // coach turn's mute/unmute stays inside the same window.
  useEffect(() => {
    if (!RECORDING_PHASES.has(phase) || !micLive) return;
    if (micResumedAfterCoachRef.current) micResumedAfterCoachRef.current = false;
    else transcriptionFailuresRef.current = 0;
  }, [phase, micLive]);

  // Only meaningful while the candidate is actually recording; anywhere else a
  // muted mic changes nothing.
  const micIssue = micLive && RECORDING_PHASES.has(phase) ? micTrackIssue : null;

  const stopAnswering = useCallback(() => {
    const stoppedAtMs = Date.now();
    setNotice(null);
    setMicEnabled(false);
    enterPhase(VoicePhase.Finalizing);
    commitInputAudioBuffer(); // before the nudge, so its item is already tracked
    // Coaching disabled: never nudge the model to speak, so no
    // CoachTranscriptDelta/OutputAudioStopped will ever arrive to move the
    // phase past Finalizing — captureWhenReady below does that itself.
    if (coachingEnabled) {
      // The whole answer's length so far, on assembleAnswer's clock. A retried
      // nudge reuses this item.
      const totalMs = elapsedOnlineMs(stoppedAtMs);
      if (totalMs > 0 && totalMs <= MAX_VOICE_SESSION_DURATION_MS) {
        sendSystemItem(buildStopAnswerLength(FRAMEWORKS[questionType], totalMs));
      }
      sendResponseRequest(RealtimeResponseRequest.Feedback);
    }

    if (finalizeGraceTimeoutRef.current !== null) clearTimeout(finalizeGraceTimeoutRef.current);

    const deadlineMs = Date.now() + MAX_FINALIZE_WAIT_MS;
    const capture = () => {
      if (finalizeGraceTimeoutRef.current !== null) {
        clearTimeout(finalizeGraceTimeoutRef.current);
        finalizeGraceTimeoutRef.current = null;
      }
      pendingCaptureRef.current = null;
      const { transcript, turnTimings } = assembleAnswer(stoppedAtMs);
      onAnswerCaptured(transcript, turnTimings);
      // enterPhase, not setPhase: a drop can run this and then read phaseRef
      // straight away (handleConnectionDrop), and must see AnswerCaptured.
      if (!coachingEnabled) enterPhase(VoicePhase.AnswerCaptured);
    };
    pendingCaptureRef.current = capture;
    const captureWhenReady = () => {
      const stillWaiting =
        pendingCandidateAnswerItemsRef.current.size > 0 || awaitingBufferCommitRef.current;
      if (stillWaiting && Date.now() < deadlineMs) {
        finalizeGraceTimeoutRef.current = setTimeout(captureWhenReady, FINALIZE_POLL_INTERVAL_MS);
        return;
      }
      finalizeGraceTimeoutRef.current = null;
      if (stillWaiting) {
        console.warn('Assembling voice answer with unfinalized transcript turns after max wait', {
          pendingItemIds: Array.from(pendingCandidateAnswerItemsRef.current),
          awaitingBufferCommitAck: awaitingBufferCommitRef.current,
        });
      }
      capture();
    };
    finalizeGraceTimeoutRef.current = setTimeout(captureWhenReady, FINALIZE_GRACE_MS);
  }, [
    coachingEnabled,
    questionType,
    commitInputAudioBuffer,
    sendSystemItem,
    elapsedOnlineMs,
    sendResponseRequest,
    assembleAnswer,
    onAnswerCaptured,
    setMicEnabled,
    enterPhase,
  ]);

  const addSessionContext = useCallback(
    (text: string) => {
      if (!coachingEnabled || dataChannelRef.current?.readyState !== 'open') return;
      sendSystemItem(text);
    },
    [coachingEnabled, sendSystemItem]
  );

  // Keeps capStopAnsweringRef current for the mic-stop timer armed in
  // connect(). Only Answering does anything: it's the one phase where the cap
  // arriving could destroy something, since an answer still being spoken when
  // the connection dies is gone for good. Stopping the mic here runs the same
  // flow as the candidate's own Stop click — trailing utterance flushed,
  // transcript finalized, answer submitted to Evaluate, coach nudged to speak
  // — with VOICE_FEEDBACK_RESERVE_MS left for that to play out. Every other
  // phase already has its answer safe and just runs on into the reserve.
  useEffect(() => {
    capStopAnsweringRef.current = () => {
      if (phaseRef.current === VoicePhase.Answering) stopAnswering();
    };
  }, [stopAnswering]);

  // Inside the reserve, the coach finishing is the end of the session: what's
  // left of the cap is this answer's feedback window, not enough to open a
  // follow-up conversation on, and holding the connection open just spends it
  // idling. AnswerCaptured is that moment — where OutputAudioStopped lands
  // once spoken feedback ends, and where the capture itself lands when
  // coaching is off and nothing is ever spoken — and it's also the first point
  // where teardown costs nothing, with the answer saved and no transcription
  // pending. Outside the reserve the flag is false and an ordinary session
  // passes through AnswerCaptured untouched.
  useEffect(() => {
    if (!inFeedbackReserveRef.current) return;
    if (phase !== VoicePhase.AnswerCaptured) return;
    teardown();
    setPhase(VoicePhase.Suspended);
  }, [phase, teardown]);

  // Mic stays muted until the discussingPhase session.update is acked (see
  // armMicEnableOnAck), so speech right after clicking Record can't land
  // before the server has actually switched off create_response: false.
  const startConversing = useCallback(() => {
    if (remoteAudioRef.current) remoteAudioRef.current.muted = false;
    const frame = controlsRef.current?.discussingPhase;
    if (!frame) {
      setMicEnabled(true);
      enterPhase(VoicePhase.Conversing);
      return;
    }
    armMicEnableOnAck(() => setMicEnabled(true));
    sendControlFrame(frame);
    enterPhase(VoicePhase.Conversing);
  }, [sendControlFrame, armMicEnableOnAck, setMicEnabled, enterPhase]);

  const pauseConversing = useCallback(() => {
    // Always safe to mute immediately, unlike enabling — and cancels any
    // enable still waiting on an ack from the startConversing that preceded
    // a fast Stop, so the mic can't go live again after Pause already won.
    cancelPendingMicEnable();
    micMutedForCoachRef.current = false;
    setMicEnabled(false);
    // Stale by Resume — the failure count restarts with the next recording window.
    setNotice(null);
    const frame = controlsRef.current?.answeringPhase;
    if (frame) sendControlFrame(frame);
    if (remoteAudioRef.current) remoteAudioRef.current.muted = true;
    if (isCoachSpeaking) {
      sendChannelPayload({ type: RealtimeOutboundEventType.ResponseCancel });
      setIsCoachSpeaking(false);
    }
    enterPhase(VoicePhase.Paused);
  }, [sendControlFrame, sendChannelPayload, cancelPendingMicEnable, setMicEnabled, isCoachSpeaking, enterPhase]);

  const toggle = useCallback(() => {
    switch (phase) {
      case VoicePhase.Idle:
      case VoicePhase.Suspended:
      case VoicePhase.Error:
        void connect();
        break;
      case VoicePhase.Answering:
        stopAnswering();
        break;
      case VoicePhase.AnswerCaptured:
      case VoicePhase.Paused:
        // Coaching disabled: there's no conversation to resume — the answer
        // is captured and the only path forward is Next Question.
        if (coachingEnabled) startConversing();
        break;
      case VoicePhase.Conversing:
        pauseConversing();
        break;
      // Connecting/Asking/Finalizing/Feedback: no candidate-driven action
      // mid-transition. Asking included deliberately — the question can't be
      // cut short, so the button is inert for as long as it is being read.
      default:
        break;
    }
  }, [phase, coachingEnabled, connect, stopAnswering, startConversing, pauseConversing]);

  const activity: VoiceActivity = useMemo(() => {
    if (isCoachSpeaking) return VoiceActivity.Speaking;
    if (isThinking) return VoiceActivity.Thinking;
    // Not "Listening" while the browser says the mic is delivering nothing.
    if (micLive && micIssue === null) return VoiceActivity.Listening;
    return VoiceActivity.Idle;
  }, [isCoachSpeaking, isThinking, micLive, micIssue]);

  return {
    phase,
    activity,
    errorMessage,
    entries,
    toggle,
    getRemoteAudioLevel,
    micIssue,
    notice,
    transcriptionFailing: notice === TRANSCRIPTION_FAILED_NOTICE && RECORDING_PHASES.has(phase),
    connectionLost:
      phase === VoicePhase.Error &&
      (errorMessage === CONNECTION_LOST_MESSAGE || errorMessage === CONNECTION_LOST_SUBMITTED_MESSAGE),
    reconnect,
    addSessionContext,
  };
}
